import { useEffect, useState } from 'react';
import { Plus, Pencil, Trash2, Image as ImageIcon, Video, X, Upload, Youtube, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import AdminLayout from '../../components/AdminLayout';
import { getMedia, createMedia, updateMedia, deleteMedia, importYouTubeChannel } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../../components/ui/tabs';

const AdminMedia = () => {
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedMedia, setSelectedMedia] = useState(null);
  const [mediaType, setMediaType] = useState('photo');
  const [channelUrl, setChannelUrl] = useState('');
  const [importing, setImporting] = useState(false);
  const [formData, setFormData] = useState({
    type: 'photo',
    title: '',
    url: '',
    image_data: '',
    thumbnail: '',
    order: 0
  });

  useEffect(() => {
    fetchMedia();
  }, []);

  const fetchMedia = async () => {
    try {
      const res = await getMedia();
      setMedia(res.data);
    } catch (error) {
      toast.error('Failed to load media');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (item = null, type = 'photo') => {
    if (item) {
      setFormData({
        type: item.type,
        title: item.title,
        url: item.url || '',
        image_data: item.image_data || '',
        thumbnail: item.thumbnail || '',
        order: item.order
      });
      setMediaType(item.type);
      setSelectedMedia(item);
    } else {
      setFormData({
        type,
        title: '',
        url: '',
        image_data: '',
        thumbnail: '',
        order: media.length
      });
      setMediaType(type);
      setSelectedMedia(null);
    }
    setDialogOpen(true);
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size must be less than 5MB');
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setFormData(prev => ({ ...prev, image_data: reader.result }));
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const data = {
      ...formData,
      type: mediaType
    };

    try {
      if (selectedMedia) {
        await updateMedia(selectedMedia.id, data);
        toast.success('Media updated successfully');
      } else {
        await createMedia(data);
        toast.success('Media added successfully');
      }
      setDialogOpen(false);
      fetchMedia();
    } catch (error) {
      toast.error('Failed to save media');
    }
  };

  const handleDelete = async () => {
    try {
      await deleteMedia(selectedMedia.id);
      toast.success('Media deleted successfully');
      setDeleteDialogOpen(false);
      fetchMedia();
    } catch (error) {
      toast.error('Failed to delete media');
    }
  };

  const handleImportChannel = async () => {
    if (!channelUrl.trim()) {
      toast.error('Please enter a YouTube channel URL');
      return;
    }

    setImporting(true);
    try {
      const res = await importYouTubeChannel(channelUrl);
      toast.success(res.data.message);
      setImportDialogOpen(false);
      setChannelUrl('');
      fetchMedia();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to import videos');
    } finally {
      setImporting(false);
    }
  };

  const photos = media.filter(m => m.type === 'photo');
  const videos = media.filter(m => m.type === 'video');

  const getYouTubeId = (url) => {
    const match = url?.match(/(?:youtu\.be\/|youtube\.com(?:\/embed\/|\/v\/|\/watch\?v=|\/watch\?.+&v=))([\w-]{11})/);
    return match ? match[1] : null;
  };

  return (
    <AdminLayout>
      <div data-testid="admin-media-page">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold" style={{ fontFamily: 'Syne, sans-serif' }}>
              Media
            </h1>
            <p className="text-neutral-500 mt-2">Manage your photos and videos</p>
          </div>
          <div className="flex gap-2">
            <Button 
              onClick={() => handleOpenDialog(null, 'photo')}
              variant="outline"
              data-testid="add-photo-btn"
            >
              <ImageIcon className="w-4 h-4 mr-2" />
              Add Photo
            </Button>
            <Button 
              onClick={() => handleOpenDialog(null, 'video')}
              variant="outline"
              data-testid="add-video-btn"
            >
              <Video className="w-4 h-4 mr-2" />
              Add Video
            </Button>
            <Button 
              onClick={() => setImportDialogOpen(true)}
              className="bg-red-600 hover:bg-red-700"
              data-testid="import-channel-btn"
            >
              <Youtube className="w-4 h-4 mr-2" />
              Import Channel
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-16">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-neutral-900"></div>
          </div>
        ) : (
          <Tabs defaultValue="photos" className="w-full">
            <TabsList>
              <TabsTrigger value="photos" data-testid="photos-tab">
                Photos ({photos.length})
              </TabsTrigger>
              <TabsTrigger value="videos" data-testid="videos-tab">
                Videos ({videos.length})
              </TabsTrigger>
            </TabsList>

            <TabsContent value="photos" className="mt-6">
              {photos.length === 0 ? (
                <div className="admin-card text-center py-16">
                  <ImageIcon className="w-12 h-12 text-neutral-300 mx-auto mb-4" />
                  <p className="text-neutral-500 mb-4">No photos yet</p>
                  <Button onClick={() => handleOpenDialog(null, 'photo')}>Upload your first photo</Button>
                </div>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {photos.map((photo) => (
                    <div 
                      key={photo.id}
                      className="group relative aspect-square bg-neutral-100 rounded-lg overflow-hidden"
                      data-testid={`media-photo-${photo.id}`}
                    >
                      <img 
                        src={photo.image_data || photo.thumbnail} 
                        alt={photo.title}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/50 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
                        <div className="flex gap-2">
                          <Button 
                            size="icon" 
                            variant="secondary"
                            onClick={() => handleOpenDialog(photo)}
                            data-testid={`edit-photo-${photo.id}`}
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button 
                            size="icon" 
                            variant="destructive"
                            onClick={() => {
                              setSelectedMedia(photo);
                              setDeleteDialogOpen(true);
                            }}
                            data-testid={`delete-photo-${photo.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                      <div className="absolute bottom-0 left-0 right-0 p-2 bg-gradient-to-t from-black/70 to-transparent">
                        <p className="text-white text-sm truncate">{photo.title}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>

            <TabsContent value="videos" className="mt-6">
              {videos.length === 0 ? (
                <div className="admin-card text-center py-16">
                  <Video className="w-12 h-12 text-neutral-300 mx-auto mb-4" />
                  <p className="text-neutral-500 mb-4">No videos yet</p>
                  <div className="flex gap-2 justify-center">
                    <Button onClick={() => handleOpenDialog(null, 'video')}>Add single video</Button>
                    <Button 
                      onClick={() => setImportDialogOpen(true)}
                      className="bg-red-600 hover:bg-red-700"
                    >
                      <Youtube className="w-4 h-4 mr-2" />
                      Import from channel
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {videos.map((video) => {
                    const videoId = getYouTubeId(video.url);
                    const thumbnail = video.thumbnail || (videoId ? `https://img.youtube.com/vi/${videoId}/mqdefault.jpg` : null);
                    
                    return (
                      <div 
                        key={video.id}
                        className="admin-card"
                        data-testid={`media-video-${video.id}`}
                      >
                        <div className="aspect-video bg-neutral-100 rounded-lg overflow-hidden mb-4">
                          {thumbnail && (
                            <img 
                              src={thumbnail} 
                              alt={video.title}
                              className="w-full h-full object-cover"
                            />
                          )}
                        </div>
                        <div className="flex justify-between items-start">
                          <div className="flex-1 min-w-0">
                            <h3 className="font-semibold truncate">{video.title}</h3>
                            <p className="text-sm text-neutral-500 truncate">{video.url}</p>
                          </div>
                          <div className="flex gap-2 ml-2">
                            <Button 
                              size="icon" 
                              variant="outline"
                              onClick={() => handleOpenDialog(video)}
                              data-testid={`edit-video-${video.id}`}
                            >
                              <Pencil className="w-4 h-4" />
                            </Button>
                            <Button 
                              size="icon" 
                              variant="outline"
                              className="text-red-600"
                              onClick={() => {
                                setSelectedMedia(video);
                                setDeleteDialogOpen(true);
                              }}
                              data-testid={`delete-video-${video.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </TabsContent>
          </Tabs>
        )}

        {/* Media Form Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {selectedMedia ? 'Edit' : 'Add'} {mediaType === 'photo' ? 'Photo' : 'Video'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="title">Title</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="e.g., Wedding at Grand Hotel"
                  required
                  data-testid="media-title-input"
                />
              </div>

              {mediaType === 'photo' ? (
                <div className="space-y-2">
                  <Label>Photo</Label>
                  <div className="border-2 border-dashed border-neutral-200 rounded-lg p-6 text-center">
                    {formData.image_data ? (
                      <div className="relative">
                        <img 
                          src={formData.image_data} 
                          alt="Preview"
                          className="max-h-48 mx-auto rounded"
                        />
                        <Button
                          type="button"
                          size="icon"
                          variant="destructive"
                          className="absolute top-0 right-0"
                          onClick={() => setFormData(prev => ({ ...prev, image_data: '' }))}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    ) : (
                      <label className="cursor-pointer block">
                        <Upload className="w-8 h-8 text-neutral-400 mx-auto mb-2" />
                        <p className="text-sm text-neutral-500">Click to upload image</p>
                        <p className="text-xs text-neutral-400 mt-1">Max 5MB</p>
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={handleFileUpload}
                          data-testid="media-file-input"
                        />
                      </label>
                    )}
                  </div>
                </div>
              ) : (
                <div className="space-y-2">
                  <Label htmlFor="url">YouTube URL</Label>
                  <Input
                    id="url"
                    value={formData.url}
                    onChange={(e) => setFormData(prev => ({ ...prev, url: e.target.value }))}
                    placeholder="https://www.youtube.com/watch?v=..."
                    required
                    data-testid="media-url-input"
                  />
                  {formData.url && getYouTubeId(formData.url) && (
                    <div className="mt-2 aspect-video bg-neutral-100 rounded-lg overflow-hidden">
                      <img 
                        src={`https://img.youtube.com/vi/${getYouTubeId(formData.url)}/mqdefault.jpg`}
                        alt="Video thumbnail"
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}
                </div>
              )}

              <div className="flex justify-end gap-3">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-neutral-900 hover:bg-neutral-800" data-testid="save-media-btn">
                  {selectedMedia ? 'Update' : 'Add'} {mediaType === 'photo' ? 'Photo' : 'Video'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* YouTube Import Dialog */}
        <Dialog open={importDialogOpen} onOpenChange={setImportDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Youtube className="w-5 h-5 text-red-600" />
                Import from YouTube Channel
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="channelUrl">YouTube Channel URL</Label>
                <Input
                  id="channelUrl"
                  value={channelUrl}
                  onChange={(e) => setChannelUrl(e.target.value)}
                  placeholder="https://www.youtube.com/@YourChannel"
                  data-testid="channel-url-input"
                />
                <p className="text-xs text-neutral-500">
                  Supports URLs like:
                  <br />• youtube.com/@username
                  <br />• youtube.com/channel/UC...
                  <br />• youtube.com/c/ChannelName
                </p>
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm">
                <p className="text-amber-800">
                  <strong>Note:</strong> This will import all public videos from the channel. 
                  Videos that are already imported will be skipped.
                </p>
              </div>

              <div className="flex justify-end gap-3">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => {
                    setImportDialogOpen(false);
                    setChannelUrl('');
                  }}
                  disabled={importing}
                >
                  Cancel
                </Button>
                <Button 
                  onClick={handleImportChannel}
                  className="bg-red-600 hover:bg-red-700"
                  disabled={importing}
                  data-testid="confirm-import-btn"
                >
                  {importing ? (
                    <span className="flex items-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Importing...
                    </span>
                  ) : (
                    'Import Videos'
                  )}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation */}
        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Media</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete "{selectedMedia?.title}"? This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction 
                onClick={handleDelete}
                className="bg-red-600 hover:bg-red-700"
                data-testid="confirm-delete-media-btn"
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </AdminLayout>
  );
};

export default AdminMedia;
